import React, { useState, useEffect, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import TopBar from '../components/TopBar';
import DashboardHome from './DashboardHome';
import ContactsPage from './ContactsPage';
import ContactFormPage from './ContactFormPage';
import AccountPage from './AccountPage';
import FunnelPage from './FunnelPage';
import DealDetailPage from './DealDetailPage';
import { Skeleton, SkeletonCards } from '../components/ui/Skeleton';

import IncomingCallModal from '../components/IncomingCallModal';
import OutgoingCallPanel from '../components/OutgoingCallPanel';
import BottomNav from '../components/BottomNav';
import { GlobalNotifications } from '../components/NotificationCenter';
import { getSocket } from '../utils/socket';
import { isVisibleToRoles } from '../utils/permissions';

// Крупные разделы грузятся по требованию: первый экран открывается быстрее
const TasksPage = React.lazy(() => import('./TasksPage'));
const InboxPage = React.lazy(() => import('./InboxPage'));
const CallsPage = React.lazy(() => import('./CallsPage'));
const ReviewsPage = React.lazy(() => import('./ReviewsPage'));
const AnalyticsPage = React.lazy(() => import('./AnalyticsPage'));
const EducationPage = React.lazy(() => import('./EducationPage'));

const PageFallback = () => (
  <div className="p-4 sm:p-6 lg:p-8 space-y-5 max-w-7xl mx-auto"><Skeleton className="h-6 w-40" /><SkeletonCards count={4} /><Skeleton className="h-64 w-full" rounded="rounded-2xl" /></div>
);

// Modul darajasida — navigatsiya/remount da reset bo'lmaydi
const _dismissedCalls = new Set();

export function navKeyToPath(key) {
  if (key === 'dashboard') return '/dashboard';
  if (key === 'contacts')  return '/contacts';
  if (key === 'tasks')     return '/tasks';
  if (key === 'education') return '/education';
  if (key === 'inbox')     return '/inbox';
  if (key === 'calls')     return '/calls';
  if (key === 'reviews')   return '/reviews';
  if (key === 'analytics') return '/analytics/sales';
  if (key.startsWith('funnel-')) return `/funnel/${key.replace('funnel-', '')}`;
  return '/dashboard';
}

function pathToNavKey(pathname) {
  if (pathname.startsWith('/contacts')) return 'contacts';
  if (pathname.startsWith('/tasks'))    return 'tasks';
  if (pathname.startsWith('/education')) return 'education';
  if (pathname.startsWith('/inbox'))    return 'inbox';
  if (pathname.startsWith('/calls'))    return 'calls';
  if (pathname.startsWith('/reviews'))  return 'reviews';
  if (pathname.startsWith('/analytics')) return 'analytics';
  if (pathname.startsWith('/funnel/')) {
    const funnelId = pathname.split('/')[2];
    return `funnel-${funnelId}`;
  }
  return 'dashboard';
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [incomingCall, setIncomingCall] = useState(null);
  const orgId = useSelector(s => s.auth.user?.organization?.id || s.auth.user?.organization?._id);
  const user = useSelector(s => s.auth.user);
  // Kiruvchi qo'ng'iroq popup'i — Sozlamalar → Telefoniya (ATC) da tanlangan
  // rollargagina ko'rinadi (bo'sh bo'lsa — hammaga, standart xulq-atvor).
  const canSeeCallPopup = isVisibleToRoles(user, user?.organization?.atc?.popupVisibleToRoles);

  useEffect(() => {
    if (!orgId || !canSeeCallPopup) return;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
    const poll = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const res = await axios.get(`${API_URL}/atc/calls`, { params: { limit: 5 } });
        // Окно — только для ВХОДЯЩИХ: исходящие показывает панель «Идёт звонок» (OutgoingCallPanel) тому, кто звонит.
        // Берём свежий (до 90 с) звонящий входящий среди последних звонков.
        const call = (res.data.calls || []).find(c => c.direction === 'in' && c.status === 'ringing' && Date.now() - new Date(c.createdAt) < 90000);
        if (!call) return;
        if (_dismissedCalls.has(call.callId)) return;
        setIncomingCall(prev => {
          if (prev?.callId === call.callId) return prev;
          return { callId: call.callId, phone: call.phone, ext: call.ext,
            contactName: call.contact?.name || null, contactId: call.contact?._id || null,
            assignedToName: call.assignedTo?.name || null,
            direction: call.direction, groupName: call.groupName, status: call.status };
        });
      } catch(e) { console.error('[ATC poll error]', e?.response?.status, e?.message); }
    };

    // Socket events — real-time trigger for poll
    const socket = getSocket();
    const onIncoming = () => poll();
    const onEnded = ({ callId } = {}) => {
      // Tugagan callni dismissed deb belgilaymiz — poll() race condition oldini olamiz
      if (callId) _dismissedCalls.add(callId);
      setIncomingCall(prev => (prev?.callId === callId ? null : prev));
    };
    socket.on('atc:incoming', onIncoming);
    socket.on('atc:ended',    onEnded);

    // Polling — har 4 soniyada tekshir (socket ishlamasa ham ishlaydi)
    poll();
    const interval = setInterval(poll, 4000);

    return () => {
      socket.off('atc:incoming', onIncoming);
      socket.off('atc:ended',    onEnded);
      clearInterval(interval);
    };
  }, [orgId, canSeeCallPopup]);

  const activeKey = pathToNavKey(location.pathname);

  const handleNavigate = (key) => {
    navigate(navKeyToPath(key));
  };

  const handleAccountSettings = () => {
    navigate('/account');
  };

  const getContent = () => {
    const p = location.pathname;
    if (p === '/account') return <AccountPage />;
    if (p === '/tasks')   return <TasksPage />;
    if (p === '/education' || p.startsWith('/education/')) return <EducationPage />;
    if (p === '/inbox')   return <InboxPage />;
    if (p === '/calls')   return <CallsPage />;
    if (p === '/reviews') return <ReviewsPage />;
    if (p === '/analytics' || p.startsWith('/analytics/')) return <AnalyticsPage />;
    if (p.startsWith('/funnel/')) {
      // /funnel/:id/deal/new  or  /funnel/:id/deal/:dealId
      const dealMatch = p.match(/^\/funnel\/([^/]+)\/deal\/(.+)$/);
      if (dealMatch) {
        return <DealDetailPage funnelId={dealMatch[1]} dealId={dealMatch[2]} />;
      }
      const funnelId = p.replace('/funnel/', '');
      return <FunnelPage funnelId={funnelId} />;
    }
    if (p === '/contacts/new' || (p.startsWith('/contacts/') && p !== '/contacts/')) {
      return <ContactFormPage />;
    }
    if (activeKey === 'contacts') return <ContactsPage />;
    return <DashboardHome />;
  };

  return (
    <div className="flex flex-col h-screen-safe bg-surface-50 aurora overflow-hidden">
      <TopBar
        active={activeKey}
        onNavigate={handleNavigate}
        onAccountSettings={handleAccountSettings}
      />
      <main className="flex-1 min-h-0 overflow-y-auto pb-[calc(5.5rem+env(safe-area-inset-bottom))] lg:pb-0">
        <Suspense fallback={<PageFallback />}>{getContent()}</Suspense>
      </main>
      <BottomNav />
      <GlobalNotifications />
      <OutgoingCallPanel />
      {incomingCall && canSeeCallPopup && (
        <IncomingCallModal
          call={incomingCall}
          onDismiss={() => {
            if (incomingCall?.callId) _dismissedCalls.add(incomingCall.callId);
            setIncomingCall(null);
          }}
        />
      )}
    </div>
  );
}
